import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

// Optional discounted add-ons on the PDP (lm-product-addons section).
//
// A2 — multi-add: we do NOT take over the native buy flow. The shopper adds the
// main product the normal way; we listen for the native product-form's success
// CartAddEvent (source 'product-form-component', no didError) and then fire a
// SECOND /cart/add.js for whatever add-ons are ticked, each line tagged with
// _addon / _addon_for line-item properties. This avoids racing Horizon's
// document-level capturing submit listener and never blocks the main sale.
//
// A3 — cart-drawer grouping (polish): a MutationObserver re-labels add-on lines
// after each drawer morph. /cart.js is the only source of the underscore-prefixed
// properties (the core template hides them from the DOM). Re-applies from a cached
// key set synchronously after each morph to avoid flicker; refreshes the set async.
//
// Degrades gracefully in IG WebView (62% of traffic): event delegation, no new
// libraries, no hard dependency on anything a locked-down WebView restricts. If
// this module never loads, the add-on checkboxes simply do nothing and the native
// buy flow is untouched.

const CART_ADD_URL = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
const CART_URL = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';

if (!(window.customElements && customElements.get('lm-product-addons'))) {
  class LmProductAddons extends HTMLElement {
    connectedCallback() {
      this.mainHandle = this.dataset.mainHandle || '';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currency = this.dataset.currency || 'USD';

      this.rows = Array.from(this.querySelectorAll('[data-lm-addon-row]'));
      this.errorEl = this.querySelector('[data-lm-addons-error]');
      if (!this.rows.length) return;

      // Variant (colour) selection within a card — delegated change handler.
      this._onChange = this.onChange.bind(this);
      this.addEventListener('change', this._onChange);

      // React to a successful native add-to-cart, then add the ticked add-ons.
      this._onCartUpdate = this.onCartUpdate.bind(this);
      document.addEventListener('cart:update', this._onCartUpdate);

      // A3: cart-drawer decoration.
      this.initCartDecoration();
    }

    disconnectedCallback() {
      if (this._onChange) this.removeEventListener('change', this._onChange);
      if (this._onCartUpdate) document.removeEventListener('cart:update', this._onCartUpdate);
      this.teardownCartDecoration();
    }

    money(cents) {
      try {
        return formatMoney(cents, this.moneyFormat, this.currency);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    }

    // ---- A2: variant selection + multi-add ---------------------------------

    onChange(event) {
      const radio = event.target.closest('[data-lm-addon-variant]');
      if (!radio) return; // checkbox toggles need no JS — :has() styles them
      const row = radio.closest('[data-lm-addon-row]');
      if (!row) return;

      row.dataset.variantId = radio.value;
      row.querySelectorAll('.lm-addon__swatch').forEach((swatch) => {
        const input = swatch.querySelector('[data-lm-addon-variant]');
        swatch.classList.toggle('is-selected', input === radio);
      });
      this.updateRowPrice(row, radio.value);
    }

    // Each card carries its own variant→{price,compare} map (raw cents) in a
    // data-lm-variant-prices JSON attribute. Parse once and cache on the row.
    rowPrices(row) {
      if (row._lmPrices) return row._lmPrices;
      try {
        row._lmPrices = JSON.parse(row.dataset.lmVariantPrices || '{}') || {};
      } catch (e) {
        row._lmPrices = {};
      }
      return row._lmPrices;
    }

    updateRowPrice(row, variantId) {
      const data = this.rowPrices(row)[variantId];
      if (!data) return;
      const priceEl = row.querySelector('[data-lm-addon-price]');
      const compareEl = row.querySelector('[data-lm-addon-compare]');
      const saveEl = row.querySelector('[data-lm-addon-save]');
      const hasCompare = typeof data.compare === 'number' && data.compare > data.price;

      if (priceEl) priceEl.textContent = this.money(data.price);
      if (compareEl) {
        if (hasCompare) {
          compareEl.textContent = this.money(data.compare);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.setAttribute('hidden', '');
        }
      }
      if (saveEl) {
        if (hasCompare) {
          saveEl.textContent = 'Save ' + this.money(data.compare - data.price);
          saveEl.removeAttribute('hidden');
        } else {
          saveEl.setAttribute('hidden', '');
        }
      }
    }

    checkedItems() {
      const items = [];
      this.rows.forEach((row) => {
        const check = row.querySelector('[data-lm-addon-check]');
        if (!check || !check.checked) return;
        const id = Number(row.dataset.variantId);
        if (!id) return;
        items.push({
          id: id,
          quantity: 1,
          properties: { _addon: 'true', _addon_for: this.mainHandle },
        });
      });
      return items;
    }

    gatherSectionIds() {
      const ids = [];
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
      });
      return ids.join(',');
    }

    onCartUpdate(event) {
      const data = (event && event.detail && event.detail.data) || {};
      // Only react to a SUCCESSFUL native product-form add — never our own add
      // (source 'lm-addons'), bundle adds ('lm-bundle-tiers'), quantity changes,
      // or the over-max/error dispatch (didError).
      if (data.source !== 'product-form-component' || data.didError) return;
      if (this._busy) return;

      const items = this.checkedItems();
      if (!items.length) return;

      this._busy = true;
      this.setBusy(true);
      this.clearError();

      const payload = { items: items, sections: this.gatherSectionIds() };

      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response && response.status) {
            // Shopify error payload (sold out / not enough stock). The main sale
            // already succeeded — surface the add-on failure without blocking it.
            this.showError(response.description || response.message || 'Could not add the add-on.');
            return null;
          }
          this.clearChecks();
          return this.refreshAndOpenCart(response, items);
        })
        .catch(() => {
          this.showError('Could not add the add-on. Please try again.');
        })
        .finally(() => {
          this._busy = false;
          this.setBusy(false);
        });
    }

    refreshAndOpenCart(addResponse, items) {
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      // Fetch the cart for an accurate item_count (mirrors product-form.js), then
      // dispatch the event the cart drawer listens for so it repaints once.
      return fetch(CART_URL + '.js')
        .then((r) => r.json())
        .catch(() => null)
        .then((cart) => {
          document.dispatchEvent(
            new CartAddEvent(cart || undefined, this.id || 'lm-product-addons', {
              source: 'lm-addons',
              itemCount: totalQuantity,
              sections: addResponse && addResponse.sections,
            })
          );
        });
    }

    setBusy(busy) {
      this.rows.forEach((row) => {
        const check = row.querySelector('[data-lm-addon-check]');
        if (check && check.checked) {
          if (busy) row.setAttribute('aria-busy', 'true');
          else row.removeAttribute('aria-busy');
        }
      });
    }

    clearChecks() {
      this.rows.forEach((row) => {
        const check = row.querySelector('[data-lm-addon-check]');
        if (check) check.checked = false;
      });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.removeAttribute('hidden');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
    }

    // ---- A3: cart-drawer add-on labelling ----------------------------------

    initCartDecoration() {
      const components = Array.from(document.querySelectorAll('cart-items-component'));
      if (!components.length || !('MutationObserver' in window)) return;

      this._addonKeys = new Set();
      this._decoComponents = components;
      this._refreshTimer = null;
      this._refreshing = false;

      this._observers = components.map((component) => {
        const observer = new MutationObserver(() => {
          // Morph wipes our class/badge (they aren't in the server HTML), so
          // re-apply instantly from the cached key set — no flicker — then
          // refresh membership from /cart.js asynchronously.
          this.decorate();
          this.scheduleRefresh();
        });
        observer.observe(component, { childList: true, subtree: true });
        return { observer, component };
      });

      // Cart may already hold add-ons on page load.
      this.scheduleRefresh();
    }

    teardownCartDecoration() {
      if (this._refreshTimer) clearTimeout(this._refreshTimer);
      if (this._observers) this._observers.forEach(({ observer }) => observer.disconnect());
      this._observers = null;
    }

    scheduleRefresh() {
      if (this._refreshTimer) return;
      this._refreshTimer = setTimeout(() => {
        this._refreshTimer = null;
        this.refreshAddonKeys();
      }, 80);
    }

    refreshAddonKeys() {
      if (this._refreshing) return;
      this._refreshing = true;
      fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          if (!cart || !cart.items) return;
          const next = new Set();
          cart.items.forEach((item) => {
            const props = item.properties || {};
            if (props._addon && item.key) next.add(item.key);
          });
          this._addonKeys = next;
          this.decorate();
        })
        .catch(() => {})
        .finally(() => {
          this._refreshing = false;
        });
    }

    decorate() {
      if (!this._addonKeys || !this._observers) return;
      // Pause observation so our own writes don't re-trigger the observer.
      this._observers.forEach(({ observer }) => observer.disconnect());
      try {
        document.querySelectorAll('cart-items-component [data-key]').forEach((row) => {
          const key = row.getAttribute('data-key');
          const isAddon = this._addonKeys.has(key);
          const decorated = row.classList.contains('lm-addon-line');
          if (isAddon && !decorated) {
            row.classList.add('lm-addon-line');
            this.injectBadge(row);
          } else if (!isAddon && decorated) {
            row.classList.remove('lm-addon-line');
            this.removeBadge(row);
          }
        });
      } finally {
        this._observers.forEach(({ observer, component }) => {
          observer.observe(component, { childList: true, subtree: true });
        });
      }
    }

    injectBadge(row) {
      if (row.querySelector('.lm-addon-badge')) return;
      const target =
        row.querySelector('.cart-items__product-info') || row.querySelector('.cart-items__details');
      if (!target) return; // class-only treatment if the markup differs
      const badge = document.createElement('span');
      badge.className = 'lm-addon-badge';
      badge.textContent = 'Add-on';
      target.appendChild(badge);
    }

    removeBadge(row) {
      const badge = row.querySelector('.lm-addon-badge');
      if (badge) badge.remove();
    }
  }

  customElements.define('lm-product-addons', LmProductAddons);
}
